'use client';
import React, { createContext, useContext, useState, useCallback } from 'react';

export type AlertType =
  | 'Alerta Amber' |'Alerta Plateada' |'Protocolo Alba' |'Clave 10-0' |'Clave 10-1' |'Clave 03' |'Clave 07/13' |'Clave 61';

export const ALERT_TYPES: AlertType[] = [
  'Alerta Amber',
  'Alerta Plateada',
  'Protocolo Alba',
  'Clave 10-0',
  'Clave 10-1',
  'Clave 03',
  'Clave 07/13',
  'Clave 61',
];

export const ALERT_TYPE_DESCRIPTIONS: Record<AlertType, string> = {
  'Alerta Amber': 'Menor desaparecido',
  'Alerta Plateada': 'Adulto mayor desaparecido',
  'Protocolo Alba': 'Mujer desaparecida',
  'Clave 10-0': 'Persecución en curso',
  'Clave 10-1': 'Oficial necesita ayuda urgente',
  'Clave 03': 'Accidente automovilístico',
  'Clave 07/13': 'Agresión o persona con arma de fuego',
  'Clave 61': 'Manifestación o bloqueo vial',
};

export const ALERT_TYPE_COLORS: Record<AlertType, string> = {
  'Alerta Amber': '#f59e0b',
  'Alerta Plateada': '#94a3b8',
  'Protocolo Alba': '#ec4899',
  'Clave 10-0': '#ef4444',
  'Clave 10-1': '#dc2626',
  'Clave 03': '#f97316',
  'Clave 07/13': '#8b5cf6',
  'Clave 61': '#06b6d4',
};

export type AlertKind = 'notification' | 'area';

export interface AlertRecipient {
  dotId: string;
  name: string;
  acknowledged: boolean;
  acknowledgedAt?: string;
}

export interface SentAlert {
  id: string;
  name: string;
  types: AlertType[];
  message: string;
  kind: AlertKind;
  recipients: AlertRecipient[];
  sentAt: string;
  zone?: string;
  areaShape?: 'polygon' | 'circle';
}

// Per-dot active alert state
export interface DotAlertState {
  alertId: string;
  types: AlertType[];
  acknowledged: boolean;
}

interface AlertContextValue {
  sentAlerts: SentAlert[];
  dotAlerts: Record<string, DotAlertState>; // keyed by dotId
  sendAlert: (alert: Omit<SentAlert, 'id' | 'sentAt'>) => void;
  acknowledgeAlert: (dotId: string) => void;
  clearDotAlert: (dotId: string) => void;
}

const AlertContext = createContext<AlertContextValue | null>(null);

export function AlertProvider({ children }: { children: React.ReactNode }) {
  const [sentAlerts, setSentAlerts] = useState<SentAlert[]>([]);
  const [dotAlerts, setDotAlerts] = useState<Record<string, DotAlertState>>({});

  const sendAlert = useCallback((alertData: Omit<SentAlert, 'id' | 'sentAt'>) => {
    const id = `alert-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const sentAt = new Date().toISOString();
    const newAlert: SentAlert = { ...alertData, id, sentAt };
    setSentAlerts(prev => [newAlert, ...prev]);

    // Set active alert state for each recipient dot
    setDotAlerts(prev => {
      const next = { ...prev };
      alertData.recipients.forEach(r => {
        next[r.dotId] = { alertId: id, types: alertData.types, acknowledged: false };
      });
      return next;
    });
  }, []);

  const acknowledgeAlert = useCallback((dotId: string) => {
    const now = new Date().toISOString();
    setDotAlerts(prev => {
      if (!prev[dotId]) return prev;
      return { ...prev, [dotId]: { ...prev[dotId], acknowledged: true } };
    });
    setSentAlerts(prev => prev.map(alert => ({
      ...alert,
      recipients: alert.recipients.map(r =>
        r.dotId === dotId ? { ...r, acknowledged: true, acknowledgedAt: now } : r
      ),
    })));
  }, []);

  const clearDotAlert = useCallback((dotId: string) => {
    setDotAlerts(prev => {
      const next = { ...prev };
      delete next[dotId];
      return next;
    });
  }, []);

  return (
    <AlertContext.Provider value={{ sentAlerts, dotAlerts, sendAlert, acknowledgeAlert, clearDotAlert }}>
      {children}
    </AlertContext.Provider>
  );
}

export function useAlerts() {
  const ctx = useContext(AlertContext);
  if (!ctx) throw new Error('useAlerts must be used within AlertProvider');
  return ctx;
}
